/**
 * iDecide - Input Field plugin
 * Adds interactive form input fields with validation to presentations
 */

import CreativeEditorSDK, {type AssetResult} from '@cesdk/cesdk-js';
import type {CreativeEngine} from '@cesdk/cesdk-js';
import {
    BLOCK_TYPE,
    PRESENTATION_WIDTH,
    PRESENTATION_HEIGHT,
    INPUT_FIELD_META_KEY,
    DYNAMIC_VARIABLES_UPDATED_EVENT
} from "../constants.ts";
import {findOwningPage, isEngineReady, splitName} from "../utils/utilities.ts";
import {positionOverlayToBlock, updateOverlayTimelineVisibility} from "../utils/interactionOverlay.ts";

const INPUT_FIELD_SOURCE_ID = 'idecide.input_field.source';
export const INPUT_FIELD_ENTRY_ID = 'idecide.input_field';

/**
 * Input field types supported
 */
export type InputFieldType = 'text' | 'number' | 'currency' | 'phone' | 'email' | 'url' | 'multi' | 'select';

export type InputFieldOption = {
    label: string;
    value: string;
};

type RawInputFieldOption = InputFieldOption | string;

export const DEFAULT_SELECT_OPTIONS: InputFieldOption[] = [
    { label: 'Option 1', value: 'Option 1' },
    { label: 'Option 2', value: 'Option 2' },
    { label: 'Option 3', value: 'Option 3' },
];

export const normalizeInputOptions = (options?: RawInputFieldOption[]): InputFieldOption[] => {
    if (!Array.isArray(options) || options.length === 0) {
        return [];
    }
    return options.map((option, index) => {
        if (typeof option === 'string') {
            const trimmed = option.trim();
            return {
                label: trimmed || `Option ${index + 1}`,
                value: trimmed || `option-${index + 1}`
            };
        }
        const label = typeof option?.label === 'string' ? option.label.trim() : '';
        const value = typeof option?.value === 'string' ? option.value.trim() : '';
        const fallbackLabel = label || value || `Option ${index + 1}`;
        const fallbackValue = value || label || fallbackLabel;
        return { label: fallbackLabel, value: fallbackValue };
    });
};

/**
 * Input Field configuration
 */
export type InputFieldMeta = {
    id: string;
    elementType: string;
    label?: string;
    labelColor?: string;
    inputType: InputFieldType;
    inputDefault?: string;
    inputPlaceholder?: string;
    inputOptions?: RawInputFieldOption[];  // For select type
    isRequired?: boolean;
    invalidMessage?: string;
    requiredMessage?: string;
    varName?: string;  // Variable name for form data storage
    style?: {
        width?: number;
        height?: number;
        fontSize?: number;
        fontColor?: string;
        backgroundColor?: string;
        borderColor?: string;
        borderWidth?: number;
        borderStyle?: string;
        borderJoin?: string;
        borderPosition?: string;
        borderRadius?: number;
        padding?: number;
        paddingX?: number;
        paddingY?: number;
        strokeEnabled?: boolean;
        opacity?: number;
    };
    validation?: {
        minLength?: number;
        maxLength?: number;
        min?: number;  // For number/currency
        max?: number;  // For number/currency
        pattern?: string;  // Regex pattern for validation
    };
};

export function defaultStyle(): Required<NonNullable<InputFieldMeta['style']>> {
    return {
        width: 300,
        height: 50,
        fontSize: 16,
        fontColor: '#333333',
        backgroundColor: '#FFFFFF',
        borderColor: '#CCCCCC',
        borderWidth: 1,
        borderStyle: 'Solid',
        borderJoin: 'Miter',
        borderPosition: 'Center',
        borderRadius: 4,
        padding: 10,
        paddingX: 10,
        paddingY: 10,
        strokeEnabled: true,
        opacity: 1,
    };
}

export function withDefaults(meta: Partial<InputFieldMeta>): InputFieldMeta {
    const inputType = meta.inputType || 'text';
    const baseStyle = {...defaultStyle(), ...(meta.style ?? {})};
    if (meta.style?.paddingX == null) {
        baseStyle.paddingX = meta.style?.padding ?? baseStyle.padding;
    }
    if (meta.style?.paddingY == null) {
        baseStyle.paddingY = meta.style?.padding ?? baseStyle.padding;
    }
    if (meta.style?.padding == null) {
        baseStyle.padding = (baseStyle.paddingX + baseStyle.paddingY) / 2;
    }
    if (inputType === 'multi' && meta.style?.height == null) {
        baseStyle.height = 120;
    }
    const normalizedOptions = normalizeInputOptions(meta.inputOptions);
    const selectOptions = normalizedOptions.length ? normalizedOptions : DEFAULT_SELECT_OPTIONS;
    const m: InputFieldMeta = {
        id: meta.id || `input-field-${Math.random().toString(36).slice(2, 8)}`,
        elementType: INPUT_FIELD_META_KEY,
        label: meta.label || '',
        labelColor: meta.labelColor ?? baseStyle.fontColor,
        inputType,
        inputDefault: meta.inputDefault,
        inputPlaceholder: meta.inputPlaceholder,
        inputOptions: meta.inputOptions ? normalizedOptions : (inputType === 'select' ? selectOptions : undefined),
        isRequired: meta.isRequired ?? false,
        invalidMessage: meta.invalidMessage || 'Please enter a valid value',
        requiredMessage: meta.requiredMessage || 'This field is required',
        varName: meta.varName,
        style: baseStyle,
        validation: meta.validation,
    }

    return m;
}

type TokenContext = {
    user?: { firstName: string; lastName: string; email: string };
    viewer?: { name: string; email: string; phone: string };
    geolocation?: { countryName: string; regionName: string; city: string };
};

type ResolvedInputFieldTokens = {
    label?: string;
    inputPlaceholder?: string;
    inputDefault?: string;
    invalidMessage?: string;
    requiredMessage?: string;
};

const formatCurrentDate = (): string => {
    return new Date().toLocaleString('en-US', { month: 'long', day: 'numeric' });
};

const formatCurrentTime = (): string => {
    return new Date()
        .toLocaleTimeString('en-US', { hour12: true, hour: 'numeric', minute: 'numeric' })
        .replace(/( AM| PM)/, '');
};

const hasToken = (value?: string | null): boolean =>
    typeof value === 'string' && value.includes('[');

const replaceTokensInString = (value: string, context: TokenContext): string => {
    if (!value || !value.includes('[')) {
        return value;
    }
    let updatedText = value;
    const userData = context.user;
    const viewerData = context.viewer;
    const geolocationData = context.geolocation;

    if (userData) {
        const fullName = `${userData.firstName} ${userData.lastName}`;
        updatedText = updatedText.replace(/\[sender-name\]/g, fullName);
        updatedText = updatedText.replace(/\[sender-name-first\]/g, userData.firstName);
        updatedText = updatedText.replace(/\[sender-name-last\]/g, userData.lastName);
        updatedText = updatedText.replace(/\[sender-email\]/g, userData.email);
    }

    if (viewerData) {
        const viewerNames = splitName(viewerData?.name ?? "");
        updatedText = updatedText.replace(/\[viewer-name\]/g, viewerData.name);
        updatedText = updatedText.replace(/\[viewer-name-first\]/g, viewerNames[0]);
        updatedText = updatedText.replace(/\[viewer-name-last\]/g, viewerNames[1]);
        updatedText = updatedText.replace(/\[viewer-email\]/g, viewerData.email);
        updatedText = updatedText.replace(/\[viewer-phone\]/g, viewerData.phone);
    }

    if (geolocationData) {
        updatedText = updatedText.replace(/\[viewer-country\]/g, geolocationData.countryName);
        updatedText = updatedText.replace(/\[viewer-region\]/g, geolocationData.regionName);
        updatedText = updatedText.replace(/\[viewer-city\]/g, geolocationData.city);
    }

    updatedText = updatedText.replace(/\[current-date\]/g, formatCurrentDate());
    updatedText = updatedText.replace(/\[current-time\]/g, formatCurrentTime());

    return updatedText;
};

const replaceInputFieldVariableTokens = (
    value: string,
    engine?: CreativeEngine | null
): string => {
    if (!engine || !value || !value.includes('[')) {
        return value;
    }
    return value.replace(/\[([^\]]+)\]/g, (match, token) => {
        const key = String(token ?? '').trim();
        if (!key) return match;
        try {
            const variableValue = engine.variable?.getString?.(`input:${key}`) ?? '';
            if (variableValue) {
                return variableValue;
            }
        } catch {}
        return match;
    });
};

const resolveInputFieldTokens = (
    meta: InputFieldMeta,
    context?: TokenContext,
    engine?: CreativeEngine
): ResolvedInputFieldTokens => {
    const needsTokens =
        hasToken(meta.label) ||
        hasToken(meta.inputPlaceholder) ||
        hasToken(meta.inputDefault) ||
        hasToken(meta.invalidMessage) ||
        hasToken(meta.requiredMessage);

    if (!needsTokens) {
        return {
            label: meta.label,
            inputPlaceholder: meta.inputPlaceholder,
            inputDefault: meta.inputDefault,
            invalidMessage: meta.invalidMessage,
            requiredMessage: meta.requiredMessage
        };
    }

    const resolveMaybe = (value?: string) =>
        value === undefined
            ? value
            : replaceInputFieldVariableTokens(
                context ? replaceTokensInString(value, context) : value,
                engine
            );

    return {
        label: resolveMaybe(meta.label),
        inputPlaceholder: resolveMaybe(meta.inputPlaceholder),
        inputDefault: resolveMaybe(meta.inputDefault),
        invalidMessage: resolveMaybe(meta.invalidMessage),
        requiredMessage: resolveMaybe(meta.requiredMessage)
    };
};

export function getInputFieldMeta(engine: CreativeEngine, blockId: number): InputFieldMeta {
    try {
        const raw = (engine.block as any).getMetadata?.(blockId, INPUT_FIELD_META_KEY);
        if (raw) return withDefaults(JSON.parse(raw));
    } catch {
    }
    return withDefaults({id: `input-field-${blockId}`});
}

const getStoredInputFieldMeta = (
    engine: CreativeEngine,
    blockId: number
): Partial<InputFieldMeta> | null => {
    try {
        const raw = (engine.block as any).getMetadata?.(blockId, INPUT_FIELD_META_KEY);
        if (raw) {
            return JSON.parse(raw) as Partial<InputFieldMeta>;
        }
    } catch {}
    return null;
};

export function setInputFieldMeta(engine: CreativeEngine, blockId: number, meta: InputFieldMeta): void {
    engine.block.setMetadata?.(blockId, INPUT_FIELD_META_KEY, JSON.stringify(withDefaults(meta)));
}

const INPUT_FIELD_OVERLAYS = new Map<number, HTMLDivElement>();
const INPUT_FIELD_HIDDEN_KEY = 'idecide/input-field-hidden';
const INPUT_FIELD_OPACITY_KEY = 'idecide/input-field-opacity';

const readBlockOpacity = (engine: CreativeEngine, blockId: number): number | undefined => {
    let opacity: number | undefined;
    try {
        const next = engine.block.getOpacity?.(blockId);
        if (typeof next === 'number' && Number.isFinite(next)) {
            opacity = next;
        }
    } catch {}
    if (opacity == null) {
        const next = engine.block.getFloat?.(blockId, 'opacity');
        if (typeof next === 'number' && Number.isFinite(next)) {
            opacity = next;
        }
    }
    return opacity;
};

const hideUnderlyingInputFieldText = (engine: CreativeEngine, blockId: number) => {
    try {
        const alreadyHidden = engine.block.getMetadata?.(blockId, INPUT_FIELD_HIDDEN_KEY) === '1';
        if (alreadyHidden) {
            const currentText = engine.block.getString?.(blockId, 'text/text') ?? '';
            const storedOpacity = engine.block.getMetadata?.(blockId, INPUT_FIELD_OPACITY_KEY);
            if (!currentText && storedOpacity) {
                return;
            }
        }
    } catch {}
    try {
        const storedOpacity = engine.block.getMetadata?.(blockId, INPUT_FIELD_OPACITY_KEY);
        if (!storedOpacity) {
            const opacity = readBlockOpacity(engine, blockId);
            const resolved = typeof opacity === 'number' && Number.isFinite(opacity) ? opacity : 1;
            engine.block.setMetadata?.(blockId, INPUT_FIELD_OPACITY_KEY, String(resolved));
        }
    } catch {}
    try {
        engine.block.setString?.(blockId, 'text/text', '');
    } catch {}
    try {
        engine.block.setMetadata?.(blockId, INPUT_FIELD_HIDDEN_KEY, '1');
    } catch {}
};

export function installInputFieldPlugin(
    cesdk: CreativeEditorSDK,
    options: {
        mountInteractiveOverlay?: boolean;
        autoOpenLibrary?: boolean;
        interactive?: boolean;
        draggable?: boolean;
        replaceTokens?: boolean;
        tokenContext?: TokenContext;
        syncMetaStyle?: boolean;
    } = {}
): { updateForActivePage: (pageId: number) => void; cleanup: () => void } {
    addInputFieldAssetSource(cesdk);

    // Asset Library entry
    cesdk.ui.addAssetLibraryEntry({
        id: INPUT_FIELD_ENTRY_ID,
        sourceIds: [INPUT_FIELD_SOURCE_ID],
        title: () => 'Form Fields',
        previewLength: 7,
        gridColumns: 2,
        gridItemHeight: 'square',
        cardBorder: true,
        cardLabel: (asset: any) => asset.label,
        cardLabelStyle: () => ({horizontalAlignment: 'center'}),
        // cardBackgroundPreferences: [{type: 'color', color: '#F0F0F0'}],
        previewBackgroundType: 'cover',
    });

    // Install interactive overlay if requested
    if (options.mountInteractiveOverlay !== false) {
        const engine = cesdk.engine;
        let overlayContainer = document.getElementById('overlay-container');
        if (!overlayContainer) {
            const host = document.getElementById("root-shadow");
            const root = host?.shadowRoot || (host as any)?.__shadowRoot;
            overlayContainer = root?.getElementById('overlay-container');
        }
        const isInteractive = options.interactive ?? true;
        const isDraggable = options.draggable ?? false;
        return mountInputFieldOverlays(engine, {
            container: overlayContainer,
            interactive: isInteractive,
            draggable: isDraggable,
            replaceTokens: options.replaceTokens ?? false,
            tokenContext: options.tokenContext,
            clearHTMLOverlays: true,
            syncMetaStyle: options.syncMetaStyle
        });
    }

    return {
        updateForActivePage: () => {},
        cleanup: () => {}
    };
}

function addInputFieldAssetSource(cesdk: CreativeEditorSDK): void {
    const engine = cesdk.engine as CreativeEngine;

    const inputFieldAssets = [
        {
            id: 'idecide.input_field.text',
            locale: 'en',
            label: 'Text Input',
            tags: ['form', 'input', 'text'],
            meta: {
                inputType: 'text' as InputFieldType,
                blockType: '//ly.img.ubq/text',
            },
        },
        {
            id: 'idecide.input_field.number',
            locale: 'en',
            label: 'Number Input',
            tags: ['form', 'input', 'number'],
            meta: {
                inputType: 'number' as InputFieldType,
                blockType: '//ly.img.ubq/text',
            },
        },
        {
            id: 'idecide.input_field.currency',
            locale: 'en',
            label: 'Currency Input',
            tags: ['form', 'input', 'currency', 'money'],
            meta: {
                inputType: 'currency' as InputFieldType,
                blockType: '//ly.img.ubq/text',
            },
        },
        {
            id: 'idecide.input_field.phone',
            locale: 'en',
            label: 'Phone Input',
            tags: ['form', 'input', 'phone', 'telephone'],
            meta: {
                inputType: 'phone' as InputFieldType,
                blockType: '//ly.img.ubq/text',
            },
        },
        {
            id: 'idecide.input_field.email',
            locale: 'en',
            label: 'Email Input',
            tags: ['form', 'input', 'email'],
            meta: {
                inputType: 'email' as InputFieldType,
                blockType: '//ly.img.ubq/text',
            },
        },
        {
            id: 'idecide.input_field.url',
            locale: 'en',
            label: 'URL Input',
            tags: ['form', 'input', 'url', 'link'],
            meta: {
                inputType: 'url' as InputFieldType,
                blockType: '//ly.img.ubq/text',
            },
        },
        {
            id: 'idecide.input_field.multi',
            locale: 'en',
            label: 'Multi-line Text',
            tags: ['form', 'input', 'textarea', 'multiline'],
            meta: {
                inputType: 'multi' as InputFieldType,
                blockType: '//ly.img.ubq/text',
            },
        },
    ];

    const source: any = {
        id: INPUT_FIELD_SOURCE_ID,
        findAssets: async (queryData: any) => ({
            assets: inputFieldAssets,
            total: inputFieldAssets.length,
            currentPage: queryData?.page ?? 0,
            nextPage: undefined
        }),

        /** Create and return an input field block. */
        applyAsset: async (assetResult: AssetResult) => {
            const page = engine.scene.getCurrentPage?.();
            if (page) {
                const inputType = assetResult.meta?.inputType as InputFieldType || 'text';
                const block = createInputFieldBlock(engine, page, {inputType});
                return block;
            }
        }
    };

    try {
        const existing: any = (engine as any).asset?.findAllSources?.();
        if (!existing || (Array.isArray(existing) && !existing.includes(INPUT_FIELD_SOURCE_ID))) {
            engine.asset.addSource(source);
        }
    } catch (e) {
        console.error('Error adding input field asset source.', e);
    }
}

export function createInputFieldBlock(
    engine: CreativeEngine,
    parentPage: number,
    meta?: Partial<InputFieldMeta>
): number {
    // Stable id for this element
    const id = `input-field-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
    const inputFieldMeta: InputFieldMeta = withDefaults({id, ...meta});

    // Create the underlying text block (will be hidden, overlay shows actual input)
    const block = engine.block.create('text');

    setInputFieldMeta(engine, block, inputFieldMeta);

    // Name in layer panel
    const inputTypeLabel = inputFieldMeta.inputType.charAt(0).toUpperCase() + inputFieldMeta.inputType.slice(1);
    engine.block.setName?.(block, `${inputTypeLabel} Input`);

    // Set placeholder text
    const placeholderText = inputFieldMeta.inputPlaceholder || `Enter ${inputFieldMeta.inputType}...`;
    engine.block.setString?.(block, 'text/text', placeholderText);

    // Set default size
    const width = inputFieldMeta.style?.width || 300;
    const height = inputFieldMeta.inputType === 'multi' ? 120 : (inputFieldMeta.style?.height || 50);
    const pageW = engine.block.getWidth?.(parentPage) ?? PRESENTATION_WIDTH;
    const pageH = engine.block.getHeight?.(parentPage) ?? PRESENTATION_HEIGHT;

    // Position in center of page
    const posX = (pageW - width) / 2;
    const posY = (pageH - height) / 2;

    engine.block.setWidthMode?.(block, 'Absolute');
    engine.block.setHeightMode?.(block, 'Absolute');
    engine.block.setWidth?.(block, width);
    engine.block.setHeight?.(block, height);
    engine.block.setPositionX?.(block, posX);
    engine.block.setPositionY?.(block, posY);

    // Style the text block
    try {
        const fontSize = inputFieldMeta.style?.fontSize || 16;
        engine.block.setFloat?.(block, 'text/fontSize', fontSize);

        const fontColor = inputFieldMeta.style?.fontColor || '#333333';
        const textFill = engine.block.getFill?.(block);
        if (textFill) {
            engine.block.setColor?.(textFill, 'fill/solid/color', {
                r: parseInt(fontColor.slice(1, 3), 16) / 255,
                g: parseInt(fontColor.slice(3, 5), 16) / 255,
                b: parseInt(fontColor.slice(5, 7), 16) / 255,
                a: 1
            });
        }
    } catch (e) {
        console.error('Error styling input field text:', e);
    }

    // Set duration to match page
    const parentDuration = engine.block.getDuration(parentPage);
    if (parentDuration > 0) {
        engine.block.setDuration(block, parentDuration);
    }

    // Attach to page & mark type
    engine.block.appendChild?.(parentPage, block);
    engine.block.setMetadata(block, BLOCK_TYPE, INPUT_FIELD_META_KEY);

    hideUnderlyingInputFieldText(engine, block);

    engine.block.setScopeEnabled(block, 'layer/rotate', false);

    return block;
}

export function safeHasInputFieldMeta(engine: CreativeEngine, id: number): boolean {
    try {
        const raw = (engine.block as any).getMetadata?.(id, INPUT_FIELD_META_KEY);
        if (!raw) return false;
        try {
            JSON.parse(raw);
        } catch {
            return false;
        }
        return true;
    } catch {
        return false;
    }
}

/** Find every block in the scene that is an input field */
export function findAllInputFields(engine: any): number[] {
    const pages: number[] = engine.scene?.getPages?.() ?? [];
    const out: number[] = [];
    const stack: number[] = [...pages];
    while (stack.length) {
        const id = stack.pop()!;
        if (!engine.block.isValid?.(id)) {
            continue;
        }
        if (safeHasInputFieldMeta(engine, id)) out.push(id);
        let blockType = '';
        try {
            blockType = engine.block.getType?.(id) ?? '';
        } catch {}
        const isFillBlock = typeof blockType === 'string' && blockType.startsWith('//ly.img.ubq/fill/');
        try {
            if (!isFillBlock) {
                const children: number[] = engine.block.getChildren?.(id) ?? [];
                for (const child of children) {
                    stack.push(child);
                }
            }
        } catch {}
        try {
            if (engine.block.supportsFill?.(id)) {
                const fill = engine.block.getFill?.(id);
                if (fill != null) {
                    stack.push(fill);
                }
            }
        } catch {}
    }
    return out;
}

const mapInputTypeToHtml = (inputType: InputFieldType): string => {
    switch (inputType) {
        case 'email':
            return 'email';
        case 'url':
            return 'url';
        case 'number':
            return 'number';
        default:
            return 'text';
    }
};

const mirrorInputValue = (engine: CreativeEngine, meta: InputFieldMeta, value: string) => {
    try {
        engine.variable?.setString?.(`input:${meta.id}`, value);
    } catch {}
    if (meta.varName) {
        try {
            engine.variable?.setString?.(`input:${meta.varName}`, value);
        } catch {}
    }
    if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent(DYNAMIC_VARIABLES_UPDATED_EVENT, {
            detail: {
                id: meta.id,
                varName: meta.varName ?? "",
                value
            }
        }));
    }
};

const updateValidationUI = (opts: {
    wrap: HTMLDivElement;
    inputEl: HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;
    errorEl: HTMLDivElement;
    handleEl?: HTMLDivElement | null;
    style: { borderWidth: number; borderColor: string };
    show: boolean;
    message?: string;
}) => {
    const { wrap, inputEl, errorEl, handleEl, style, show, message } = opts;
    const parsedBorderWidth = Number(wrap.dataset.renderBorderWidth ?? wrap.dataset.baseBorderWidth);
    const borderWidth = Math.max(
        0,
        Number.isFinite(parsedBorderWidth) ? parsedBorderWidth : style.borderWidth
    );
    const borderColor = wrap.dataset.baseBorderColor || style.borderColor;
    const borderDisabled = wrap.dataset.disableBorder === '1';
    const borderLayer = wrap.querySelector('.input-field-border-layer') as SVGSVGElement | null;
    if (show) {
        errorEl.textContent = message ?? '';
        errorEl.style.display = 'block';
        wrap.classList.add('has-error');
        inputEl.classList.add('is-invalid');
        if (borderLayer) {
            borderLayer.style.display = 'none';
        }
        inputEl.style.border = `${Math.max(1, borderWidth)}px solid #e14646`;
    } else {
        errorEl.textContent = '';
        errorEl.style.display = 'none';
        wrap.classList.remove('has-error');
        inputEl.classList.remove('is-invalid');
        inputEl.style.border = 'none';
        if (borderLayer) {
            updateInputFieldBorderLayer(wrap);
        } else {
            inputEl.style.border = borderDisabled || borderWidth <= 0
                ? 'none'
                : `${borderWidth}px solid ${borderColor}`;
        }
    }
    if (handleEl) {
        handleEl.style.top = show ? 'calc(100% + 24px)' : 'calc(100% + 6px)';
    }
};

const findInputFieldsForPage = (engine: CreativeEngine, pageId: number): number[] => {
    const out: number[] = [];
    const stack: number[] = [pageId];
    while (stack.length) {
        const id = stack.pop()!;
        if (!engine.block.isValid?.(id)) {
            continue;
        }
        if (safeHasInputFieldMeta(engine, id)) {
            out.push(id);
        }
        try {
            const children = engine.block.getChildren?.(id) ?? [];
            for (const child of children) {
                stack.push(child);
            }
        } catch {}
        try {
            if (engine.block.supportsFill?.(id)) {
                const fill = engine.block.getFill?.(id);
                if (fill != null) {
                    stack.push(fill);
                }
            }
        } catch {}
    }
    return out;
};

export function mountInputFieldOverlays(
    engine: CreativeEngine,
    opts: {
        container?: HTMLElement | null;
        interactive?: boolean;
        draggable?: boolean;
        replaceTokens?: boolean;
        tokenContext?: TokenContext;
        clearHTMLOverlays?: boolean;
        syncMetaStyle?: boolean;
    } = {}
): { updateForActivePage: (pageId: number) => void; cleanup: () => void } {
    const host = opts.container ?? (engine as any).element?.parentElement ?? document.body;
    const ownsHost = !opts.container;
    let clearOverlays = opts.clearHTMLOverlays;
    const overlays = INPUT_FIELD_OVERLAYS;
    let currentPageId = engine.scene?.getCurrentPage?.() ?? 0;

    const getActivePageFields = () =>
        currentPageId ? findInputFieldsForPage(engine, currentPageId) : [];

    const rebuild = () => {
        const all: number[] = getActivePageFields();
        if (clearOverlays) {
            overlays.forEach((el) => {
                try { el.remove(); } catch {}
            });
            overlays.clear();
            clearOverlays = false;
        } else {
            [...overlays.keys()].forEach((id) => {
                if (!all.includes(id)) {
                    overlays.get(id)?.remove();
                    overlays.delete(id);
                }
            });
        }

        all.forEach((id) => {
            if (!overlays.has(id)) {
                const el = createInputFieldOverlay(
                    engine,
                    id,
                    host,
                    !!opts.interactive,
                    !!opts.draggable,
                    !!opts.replaceTokens,
                    opts.tokenContext
                );
                if (opts.syncMetaStyle) {
                    el.dataset.syncMeta = '1';
                }
                overlays.set(id, el);
                requestAnimationFrame(() => positionOverlayToBlock(engine, id, el, updateInputFieldOverlayGeometry));
            }
        });
    };

    const layoutAll = () =>
        overlays.forEach((el, id) => positionOverlayToBlock(engine, id, el, updateInputFieldOverlayGeometry));

    const updateSelectionState = () => {
        const selectedIds = new Set(engine.block.findAllSelected?.() ?? []);
        overlays.forEach((el, id) => {
            const isSelected = selectedIds.has(id);
            el.classList.toggle('is-selected', isSelected);
            updateInputFieldSelectionHandleOverlay(el);
            const handle = el.querySelector('.input-field-drag-handle') as HTMLElement | null;
            if (handle) {
                handle.style.display = isSelected ? 'flex' : 'none';
            }
        });
    };

    let scheduled = false;
    const schedule = () => {
        if (scheduled) return;
        scheduled = true;
        requestAnimationFrame(() => {
            scheduled = false;
            rebuild();
            layoutAll();
            updateSelectionState();
            updateOverlayTimelineVisibility(engine, INPUT_FIELD_OVERLAYS, isEngineReady);
        });
    };

    schedule();

    let currentFields: number[] = [];
    let unsubFields: (() => void) | null = null;

    const updateFieldSubscriptions = () => {
        const fields = getActivePageFields();
        if (JSON.stringify(fields) !== JSON.stringify(currentFields)) {
            try { unsubFields?.(); } catch {}
            currentFields = fields;
            unsubFields = fields.length > 0
                ? engine.event?.subscribe?.(fields, (events) => {
                    try {
                        const validEvents = events.filter(e => {
                            try {
                                const id = e.block;
                                if (!Number.isInteger(id) || id <= 0 || id === 4294967295) {
                                    return false;
                                }
                                return engine.block.isValid?.(id);
                            } catch {
                                return false;
                            }
                        });
                        const hasUpdated = validEvents.some(e => e.type === 'Updated');
                        if (hasUpdated) {
                            schedule();
                        }
                    } catch (error) {
                        console.warn('Input field event handler error:', error);
                    }
                })
                : null;
        }
    };

    let unsubPages: (() => void) | null = null;

    const updateForActivePage = (pageId: number) => {
        currentPageId = pageId;
        const all: number[] = getActivePageFields();
        [...overlays.keys()].forEach((id) => {
            if (!all.includes(id)) {
                overlays.get(id)?.remove();
                overlays.delete(id);
            }
        });

        try { unsubPages?.(); } catch {}

        unsubPages = engine.event?.subscribe?.([pageId], (events) => {
            try {
                const validEvents = events.filter(e => {
                    try {
                        const id = e.block;
                        if (!Number.isInteger(id) || id <= 0 || id === 4294967295) {
                            return false;
                        }
                        return engine.block.isValid?.(id);
                    } catch {
                        return false;
                    }
                });

                const hasUpdated = validEvents.some(e => e.type === 'Updated');
                if (hasUpdated) {
                    schedule();
                    requestAnimationFrame(() => {
                        updateFieldSubscriptions();
                    });
                }
            } catch (error) {
                console.warn('Input field page event handler error:', error);
            }
        });

        requestAnimationFrame(() => {
            rebuild();
            layoutAll();
            updateSelectionState();
            updateOverlayTimelineVisibility(engine, INPUT_FIELD_OVERLAYS, isEngineReady);
            updateFieldSubscriptions();
        });
    };

    const initialPage = engine.scene?.getCurrentPage?.();
    if (initialPage) {
        updateForActivePage(initialPage);
    }
    updateFieldSubscriptions();

    let unsubSelection: (() => void) | null = null;
    try {
        const maybeUnsub = engine.block.onSelectionChanged?.(() => {
            updateSelectionState();
        });
        if (typeof maybeUnsub === 'function') {
            unsubSelection = maybeUnsub;
        }
    } catch {}

    const handleResize = () => {
        requestAnimationFrame(() => {
            layoutAll();
        });
    };

    const ro = new ResizeObserver(handleResize);
    ro.observe(host);

    const wrapper = host.closest('.cesdkWrapperStyle');
    if (wrapper) {
        ro.observe(wrapper);
    }

    window.addEventListener('cesdk-relayout', handleResize);

    let zoomTimeout: number | null = null;
    const unsubZoomLevelChanged = engine.scene?.onZoomLevelChanged?.(() => {
        if (zoomTimeout) clearTimeout(zoomTimeout);
        zoomTimeout = window.setTimeout(() => {
            layoutAll();
        }, 50);
    });

    let visibilityRafId: number | null = null;
    let unsubStateChanged: (() => void) | null = null;

    const fields = findAllInputFields(engine);
    if (fields.length > 0) {
        const rootEl = document.getElementById('root-shadow');
        const shadowRoot = rootEl?.shadowRoot;
        const playheadElement = shadowRoot?.querySelector('[role="slider"][aria-valuenow]');
        const reviewWrapper = document.querySelector('.review-cesdk-wrapper[data-playback-time]');
        const timeElement = playheadElement || reviewWrapper;

        if (timeElement) {
            const timeAttribute = playheadElement ? 'aria-valuenow' : 'data-playback-time';
            let lastProcessedTime = -1;

            const timeObserver = new MutationObserver((mutations) => {
                const timeChanged = mutations.some(mutation =>
                    mutation.type === 'attributes' && mutation.attributeName === timeAttribute
                );

                if (timeChanged) {
                    const currentTimeValue = timeElement.getAttribute(timeAttribute);
                    if (!currentTimeValue) return;

                    const parsedTime = parseFloat(currentTimeValue);
                    const roundedTime = Math.round(parsedTime * 10) / 10;
                    if (roundedTime === lastProcessedTime) {
                        return;
                    }
                    lastProcessedTime = roundedTime;

                    if (visibilityRafId !== null) {
                        cancelAnimationFrame(visibilityRafId);
                    }
                    visibilityRafId = requestAnimationFrame(() => {
                        visibilityRafId = null;
                        updateOverlayTimelineVisibility(engine, INPUT_FIELD_OVERLAYS, isEngineReady);
                    });
                }
            });

            timeObserver.observe(timeElement, {
                attributes: true,
                attributeFilter: [timeAttribute]
            });

            unsubStateChanged = () => {
                timeObserver.disconnect();
            };
        }
    }

    updateOverlayTimelineVisibility(engine, INPUT_FIELD_OVERLAYS, isEngineReady);

    return {
        updateForActivePage,
        cleanup: () => {
            window.removeEventListener('cesdk-relayout', handleResize);
            try { unsubFields?.(); } catch {}
            try { unsubPages?.(); } catch {}
            try { ro.disconnect(); } catch {}
            try { unsubZoomLevelChanged?.(); } catch {}
            if (zoomTimeout) clearTimeout(zoomTimeout);
            try { unsubStateChanged?.(); } catch {}
            if (visibilityRafId !== null) {
                cancelAnimationFrame(visibilityRafId);
                visibilityRafId = null;
            }
            try { unsubSelection?.(); } catch {}
            overlays.forEach((el) => { if (el.parentElement === host) el.remove(); });
            if (ownsHost && host.parentElement) host.parentElement.removeChild(host);
        }
    };
}

export function createInputFieldOverlay(
    engine: CreativeEngine,
    inputFieldId: number,
    root: HTMLElement,
    interactive: boolean,
    draggable: boolean,
    replaceTokens: boolean = false,
    tokenContext?: TokenContext
): HTMLDivElement {
    const wrap = document.createElement('div');
    wrap.className = 'input-field-overlay';
    wrap.style.position = 'absolute';
    wrap.style.pointerEvents = 'none';
    wrap.style.willChange = 'left,top,width,height';
    wrap.style.overflow = 'visible';
    wrap.style.zIndex = 'auto';
    wrap.style.isolation = 'isolate';

    const storedMeta = getStoredInputFieldMeta(engine, inputFieldId);
    const meta = storedMeta ? withDefaults(storedMeta) : withDefaults({id: `input-field-${inputFieldId}`});
    const storedStyle = storedMeta?.style ?? {};
    let initialOpacity: number | undefined;
    try {
        const storedOpacity = engine.block.getMetadata?.(inputFieldId, INPUT_FIELD_OPACITY_KEY);
        const storedValue = Number(storedOpacity);
        if (Number.isFinite(storedValue)) {
            initialOpacity = storedValue;
        }
    } catch {}
    if (initialOpacity == null) {
        const metaOpacity = meta?.style?.opacity;
        if (typeof metaOpacity === 'number' && Number.isFinite(metaOpacity) && metaOpacity !== 1) {
            initialOpacity = metaOpacity;
        }
    }
    if (initialOpacity == null) {
        initialOpacity = readBlockOpacity(engine, inputFieldId);
    }
    const resolvedOpacity = typeof initialOpacity === 'number' && Number.isFinite(initialOpacity)
        ? clamp01(initialOpacity)
        : 1;

    hideUnderlyingInputFieldText(engine, inputFieldId);
    const style = {...defaultStyle(), ...(meta.style ?? {})};
    const stylePaddingX = style.paddingX ?? style.padding;
    const stylePaddingY = style.paddingY ?? style.padding;
    const stroke = readInputFieldStrokeAppearance(engine, inputFieldId);
    const baseBorderWidth = Math.max(0, storedStyle.borderWidth ?? stroke.width ?? style.borderWidth);
    const baseBorderColor = storedStyle.borderColor || stroke.color || style.borderColor;
    const baseBorderStyle = storedStyle.borderStyle ?? stroke.style ?? style.borderStyle ?? 'Solid';
    const baseBorderJoin = storedStyle.borderJoin ?? stroke.join ?? style.borderJoin ?? 'Miter';
    const baseBorderPosition = storedStyle.borderPosition ?? stroke.position ?? style.borderPosition ?? 'Center';
    const strokeEnabled = storedStyle.strokeEnabled ?? stroke.enabled ?? style.strokeEnabled ?? true;
    const borderDisabled = strokeEnabled === false;
    let validationMeta = meta;
    const rawDefault = meta.inputDefault ?? '';
    const rawPlaceholder = meta.inputPlaceholder
        || (meta.inputType === 'select' ? 'Select an option' : `Enter ${meta.inputType}...`);

    wrap.dataset.idecideOverlay = 'input-field';
    wrap.dataset.blockId = String(inputFieldId);
    wrap.dataset.interactive = interactive ? '1' : '0';
    wrap.dataset.draggable = draggable ? '1' : '0';
    wrap.dataset.replaceTokens = replaceTokens ? '1' : '0';
    wrap.dataset.baseHeight = String(style.height);
    wrap.dataset.baseFontSize = String(style.fontSize);
    wrap.dataset.basePadding = String(style.padding);
    wrap.dataset.basePaddingX = String(stylePaddingX);
    wrap.dataset.basePaddingY = String(stylePaddingY);
    wrap.dataset.baseRadius = String(style.borderRadius);
    wrap.dataset.baseBorderWidth = String(baseBorderWidth);
    wrap.dataset.baseBorderColor = baseBorderColor;
    wrap.dataset.baseBorderStyle = baseBorderStyle;
    wrap.dataset.baseBorderJoin = baseBorderJoin;
    wrap.dataset.baseBorderPosition = baseBorderPosition;
    wrap.dataset.labelColor = meta.labelColor ?? '';
    wrap.dataset.baseBackground = style.backgroundColor;
    wrap.dataset.baseShadowX = '0';
    wrap.dataset.baseShadowY = '0';
    wrap.dataset.baseShadowBlur = '0';
    wrap.dataset.shadowColor = '';
    wrap.dataset.opacityOverride = '0';
    wrap.dataset.lastOpacity = '';
    wrap.dataset.opacityValue = String(resolvedOpacity);
    wrap.dataset.disableBorder = borderDisabled ? '1' : '0';
    wrap.dataset.renderBorderWidth = String(baseBorderWidth);
    wrap.dataset.renderBorderColor = baseBorderColor;
    wrap.dataset.renderRadius = String(style.borderRadius);
    if (resolvedOpacity !== 1) {
        wrap.dataset.opacityOverride = '1';
        wrap.dataset.lastOpacity = String(resolvedOpacity);
    }

    const ownerPage = findOwningPage(engine, inputFieldId);
    wrap.dataset.ownerPage = ownerPage != null ? String(ownerPage) : '';

    root.appendChild(wrap);

    const labelEl = document.createElement('div');
    labelEl.className = 'input-field-label';
    labelEl.textContent = meta.label ?? '';
    labelEl.style.position = 'absolute';
    labelEl.style.left = '0';
    labelEl.style.bottom = 'calc(100% + 6px)';
    labelEl.style.fontSize = `${Math.round(style.fontSize * 0.85)}px`;
    labelEl.style.color = meta.labelColor ?? style.fontColor;
    labelEl.style.fontWeight = '600';
    labelEl.style.pointerEvents = 'none';
    labelEl.style.display = meta.label ? 'block' : 'none';

    wrap.appendChild(labelEl);

    const inputEl =
        meta.inputType === 'multi'
            ? document.createElement('textarea')
            : meta.inputType === 'select'
                ? document.createElement('select')
                : document.createElement('input');

    inputEl.className = 'input-field-control';

    if (inputEl instanceof HTMLInputElement) {
        inputEl.type = mapInputTypeToHtml(meta.inputType);
        if (meta.inputType === 'currency') {
            inputEl.inputMode = 'decimal';
            inputEl.pattern = '[0-9]*[.,]?[0-9]*';
        }
        if (meta.inputType === 'phone') {
            inputEl.inputMode = 'numeric';
            inputEl.pattern = '\\d*';
        }
        if (meta.inputType === 'number') {
            if (meta.validation?.min !== undefined) {
                inputEl.min = String(meta.validation.min);
            }
            if (meta.validation?.max !== undefined) {
                inputEl.max = String(meta.validation.max);
            }
        }
    }

    if (inputEl instanceof HTMLTextAreaElement) {
        inputEl.rows = 3;
        inputEl.style.resize = 'none';
        if (meta.inputType === 'multi' && meta.validation?.maxLength !== undefined) {
            inputEl.maxLength = meta.validation.maxLength;
        }
    }

    if (inputEl instanceof HTMLSelectElement) {
        const options = normalizeInputOptions(meta.inputOptions);
        const resolvedOptions = options.length ? options : DEFAULT_SELECT_OPTIONS;
        const placeholderOption = document.createElement('option');
        placeholderOption.value = '';
        placeholderOption.textContent = rawPlaceholder;
        placeholderOption.disabled = true;
        placeholderOption.selected = true;
        inputEl.appendChild(placeholderOption);

        resolvedOptions.forEach((optionItem) => {
            const option = document.createElement('option');
            option.value = optionItem.value;
            option.textContent = optionItem.label;
            inputEl.appendChild(option);
        });
        let selectedValue: string | null = null;
        if (rawDefault) {
            selectedValue =
                resolvedOptions.find((opt) => opt.value === rawDefault)?.value
                ?? resolvedOptions.find((opt) => opt.label === rawDefault)?.value
                ?? null;
        }
        if (selectedValue) {
            inputEl.value = selectedValue;
        } else {
            inputEl.value = '';
        }
        mirrorInputValue(engine, meta, inputEl.value);
    }

    if (inputEl instanceof HTMLInputElement || inputEl instanceof HTMLTextAreaElement) {
        inputEl.placeholder = rawPlaceholder;
    }

    if (rawDefault) {
        if (inputEl instanceof HTMLInputElement || inputEl instanceof HTMLTextAreaElement) {
            inputEl.value = rawDefault;
            mirrorInputValue(engine, meta, rawDefault);
        }
    }

    inputEl.style.width = '100%';
    inputEl.style.height = '100%';
    inputEl.style.position = 'relative';
    inputEl.style.boxSizing = 'border-box';
    inputEl.style.fontSize = `${style.fontSize}px`;
    inputEl.style.color = style.fontColor;
    inputEl.style.background = 'transparent';
    inputEl.style.border = 'none';
    inputEl.style.borderRadius = `${style.borderRadius}px`;
    inputEl.style.padding = `${stylePaddingY}px ${stylePaddingX}px`;
    inputEl.style.pointerEvents = interactive ? 'auto' : 'none';
    inputEl.style.zIndex = '2';

    const fillEl = document.createElement('div');
    fillEl.className = 'input-field-fill-layer';
    fillEl.setAttribute('aria-hidden', 'true');
    fillEl.style.position = 'absolute';
    fillEl.style.inset = '0';
    fillEl.style.pointerEvents = 'none';
    fillEl.style.background = 'transparent';
    fillEl.style.borderRadius = `${style.borderRadius}px`;
    fillEl.style.zIndex = '0';

    const borderSvg = document.createElementNS(SVG_NS, 'svg');
    borderSvg.classList.add('input-field-border-layer');
    borderSvg.setAttribute('aria-hidden', 'true');
    borderSvg.style.position = 'absolute';
    borderSvg.style.inset = '0';
    borderSvg.style.width = '100%';
    borderSvg.style.height = '100%';
    borderSvg.style.pointerEvents = 'none';
    borderSvg.style.overflow = 'visible';
    borderSvg.style.zIndex = '1';

    const borderPath = document.createElementNS(SVG_NS, 'path');
    borderPath.classList.add('input-field-border-shape');
    borderPath.setAttribute('fill', 'none');
    borderSvg.appendChild(borderPath);

    const errorEl = document.createElement('div');
    errorEl.className = 'input-field-error';
    errorEl.style.position = 'absolute';
    errorEl.style.left = '0';
    errorEl.style.top = 'calc(100% + 6px)';
    errorEl.style.display = 'none';
    errorEl.style.color = '#e14646';
    errorEl.style.fontSize = `${Math.max(9, Math.round(style.fontSize * 0.6))}px`;
    errorEl.style.pointerEvents = 'none';

    let handleEl: HTMLDivElement | null = null;

    let touched = false;
    const applyValidation = (force = false) => {
        const value = (inputEl as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement).value ?? '';
        const result = validateInputField(value, validationMeta);
        const shouldShow = !result.valid && (force || value.trim() !== '' || validationMeta.isRequired === true);
        const message = result.message || validationMeta.invalidMessage || 'Invalid value';
        updateValidationUI({
            wrap,
            inputEl,
            errorEl,
            handleEl,
            style,
            show: shouldShow,
            message
        });
    };

    const handleValueChange = () => {
        const value = (inputEl as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement).value ?? '';
        mirrorInputValue(engine, meta, value);
    };

    const handleInputEvent = () => {
        touched = true;
        if (meta.inputType === 'multi'
            && inputEl instanceof HTMLTextAreaElement
            && meta.validation?.maxLength !== undefined
            && inputEl.value.length > meta.validation.maxLength
        ) {
            inputEl.value = inputEl.value.slice(0, meta.validation.maxLength);
        }
        handleValueChange();
        applyValidation(true);
    };

    inputEl.addEventListener('input', handleInputEvent);
    inputEl.addEventListener('change', handleInputEvent);

    const forceSelect = () => {
        try {
            const selected = engine.block.findAllSelected?.() ?? [];
            const alreadyOnly = selected.length === 1 && selected[0] === inputFieldId;
            if (alreadyOnly) {
                engine.block.setSelected?.(inputFieldId, false);
                requestAnimationFrame(() => {
                    try {
                        engine.block.setSelected?.(inputFieldId, true);
                    } catch {}
                });
                return;
            }
            if (selected.length > 0) {
                selected.forEach((id) => {
                    if (id !== inputFieldId) {
                        engine.block.setSelected?.(id, false);
                    }
                });
            }
            engine.block.setSelected?.(inputFieldId, true);
        } catch {}
    };

    inputEl.addEventListener('pointerdown', () => {
        forceSelect();
    }, { capture: true });
    inputEl.addEventListener('focus', () => {
        forceSelect();
    });
    inputEl.addEventListener('blur', () => {
        touched = true;
        applyValidation(true);
    });

    const clampNumericValue = (raw: string): string | null => {
        const num = parseNumericValue(raw);
        if (num === null) return null;
        let clamped = num;
        if (meta.validation?.min !== undefined) {
            clamped = Math.max(meta.validation.min, clamped);
        }
        if (meta.validation?.max !== undefined) {
            clamped = Math.min(meta.validation.max, clamped);
        }
        return String(clamped);
    };

    if (meta.inputType === 'currency' || meta.inputType === 'phone' || meta.inputType === 'number') {
        inputEl.addEventListener('blur', () => {
            if (!(inputEl instanceof HTMLInputElement || inputEl instanceof HTMLTextAreaElement)) return;
            let nextValue = inputEl.value;
            if (meta.inputType === 'number' || meta.inputType === 'currency') {
                const clamped = clampNumericValue(nextValue);
                if (clamped !== null) {
                    nextValue = clamped;
                }
            }
            if (meta.inputType === 'currency' || meta.inputType === 'phone') {
                nextValue = formatInputValue(nextValue, meta.inputType);
            }
            if (nextValue !== inputEl.value) {
                inputEl.value = nextValue;
                mirrorInputValue(engine, meta, nextValue);
                applyValidation(true);
            }
        });
    }

    wrap.appendChild(fillEl);
    wrap.appendChild(borderSvg);
    wrap.appendChild(inputEl);
    wrap.appendChild(errorEl);

    wrap.addEventListener('pointerdown', () => {
        forceSelect();
    });

    const startDrag = (event: PointerEvent) => {
        if (!interactive || !draggable || event.button !== 0) return;
        event.preventDefault();
        event.stopPropagation();
        try {
            engine.block.setSelected?.(inputFieldId, true);
        } catch {}

        const startX = event.clientX;
        const startY = event.clientY;
        const startPosX = engine.block.getPositionX?.(inputFieldId) ?? 0;
        const startPosY = engine.block.getPositionY?.(inputFieldId) ?? 0;
        const blockWidth = engine.block.getWidth?.(inputFieldId) ?? 1;
        const blockHeight = engine.block.getHeight?.(inputFieldId) ?? 1;
        const rect = wrap.getBoundingClientRect();
        const scaleX = rect.width / Math.max(1, blockWidth);
        const scaleY = rect.height / Math.max(1, blockHeight);

        const onMove = (moveEvent: PointerEvent) => {
            const dx = (moveEvent.clientX - startX) / (scaleX || 1);
            const dy = (moveEvent.clientY - startY) / (scaleY || 1);
            try {
                engine.block.setPositionX?.(inputFieldId, startPosX + dx);
                engine.block.setPositionY?.(inputFieldId, startPosY + dy);
            } catch {}
            requestAnimationFrame(() => positionOverlayToBlock(engine, inputFieldId, wrap, updateInputFieldOverlayGeometry));
        };

        const onUp = (upEvent: PointerEvent) => {
            try {
                (upEvent.target as HTMLElement)?.releasePointerCapture?.(upEvent.pointerId);
            } catch {}
            window.removeEventListener('pointermove', onMove);
            window.removeEventListener('pointerup', onUp);
        };

        try {
            (event.target as HTMLElement).setPointerCapture(event.pointerId);
        } catch {}
        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
    };

    if (draggable) {
        const handle = document.createElement('div');
        handle.className = 'input-field-drag-handle';
        handle.style.position = 'absolute';
        handle.style.left = '50%';
        handle.style.top = 'calc(100% + 6px)';
        handle.style.transform = 'translateX(-50%)';
        handle.style.display = 'flex';
        handle.style.alignItems = 'center';
        handle.style.justifyContent = 'center';
        handle.style.background = '#f5f8ff';
        handle.style.border = '2px solid #6f8ad6';
        handle.style.borderRadius = '6px';
        handle.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.15)';
        handle.style.cursor = 'move';
        handle.style.padding = '4px 3px 3px';
        handle.style.zIndex = '2';
        handle.style.minWidth = '20px';
        handle.style.minHeight = '10px';
        handle.style.opacity = '0.7';
        handle.style.marginTop = '5px';
        handle.style.pointerEvents = interactive && draggable ? 'auto' : 'none';
        handle.style.display = 'none';
        handle.addEventListener('pointerdown', startDrag);

        const dots = document.createElement('div');
        dots.className = 'input-field-drag-dots';
        dots.style.display = 'flex';
        dots.style.flexDirection = 'row';
        dots.style.gap = '3px';
        for (let i = 0; i < 3; i += 1) {
            const dot = document.createElement('span');
            dot.style.width = '5px';
            dot.style.height = '5px';
            dot.style.borderRadius = '50%';
            dot.style.background = '#6f8ad6';
            dots.appendChild(dot);
        }
        handle.appendChild(dots);
        wrap.appendChild(handle);
        handleEl = handle;
    }

    if (replaceTokens) {
        const resolved = resolveInputFieldTokens(meta, tokenContext, engine);
        if (resolved.label !== undefined) {
            labelEl.textContent = resolved.label ?? '';
            labelEl.style.display = resolved.label ? 'block' : 'none';
        }

        if (resolved.inputPlaceholder !== undefined) {
            if (inputEl instanceof HTMLInputElement || inputEl instanceof HTMLTextAreaElement) {
                inputEl.placeholder = resolved.inputPlaceholder ?? '';
            } else if (inputEl instanceof HTMLSelectElement) {
                const placeholderOption = inputEl.options.item(0);
                if (placeholderOption && placeholderOption.disabled) {
                    placeholderOption.textContent = resolved.inputPlaceholder ?? '';
                }
            }
        }

        if (resolved.inputDefault !== undefined) {
            const currentValue = (inputEl as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement).value ?? '';
            if (!touched && (currentValue === '' || currentValue === rawDefault)) {
                if (inputEl instanceof HTMLInputElement || inputEl instanceof HTMLTextAreaElement) {
                    inputEl.value = resolved.inputDefault ?? '';
                } else if (inputEl instanceof HTMLSelectElement) {
                    const resolvedValue = resolved.inputDefault ?? '';
                    if (resolvedValue) {
                        const options = Array.from(inputEl.options);
                        const match = options.find((opt) => opt.value === resolvedValue)
                            ?? options.find((opt) => opt.textContent === resolvedValue);
                        inputEl.value = match?.value ?? '';
                    } else {
                        inputEl.value = '';
                    }
                }
                mirrorInputValue(engine, meta, inputEl.value ?? '');
            }
        }

        validationMeta = {
            ...meta,
            invalidMessage: resolved.invalidMessage ?? meta.invalidMessage,
            requiredMessage: resolved.requiredMessage ?? meta.requiredMessage
        };
    }

    applyValidation(false);
    updateInputFieldBorderLayer(wrap);

    requestAnimationFrame(() => positionOverlayToBlock(engine, inputFieldId, wrap, updateInputFieldOverlayGeometry));

    return wrap;
}

const colorToRgbaString = (color: any): string | null => {
    if (!color) return null;
    if ('r' in color && 'g' in color && 'b' in color) {
        const a = typeof color.a === 'number' ? color.a : 1;
        return `rgba(${Math.round(color.r * 255)}, ${Math.round(color.g * 255)}, ${Math.round(color.b * 255)}, ${a})`;
    }
    if ('c' in color && 'm' in color && 'y' in color && 'k' in color) {
        const c = color.c ?? 0;
        const m = color.m ?? 0;
        const y = color.y ?? 0;
        const k = color.k ?? 0;
        const r = 1 - Math.min(1, c * (1 - k) + k);
        const g = 1 - Math.min(1, m * (1 - k) + k);
        const b = 1 - Math.min(1, y * (1 - k) + k);
        const a = typeof color.a === 'number' ? color.a : 1;
        return `rgba(${Math.round(r * 255)}, ${Math.round(g * 255)}, ${Math.round(b * 255)}, ${a})`;
    }
    if ('h' in color && 's' in color && 'l' in color) {
        const rawH = color.h ?? 0;
        const rawS = color.s ?? 0;
        const rawL = color.l ?? 0;
        const h = rawH > 1 ? rawH : rawH * 360;
        const s = rawS > 1 ? rawS / 100 : rawS;
        const l = rawL > 1 ? rawL / 100 : rawL;
        const c = (1 - Math.abs(2 * l - 1)) * s;
        const hh = h / 60;
        const x = c * (1 - Math.abs((hh % 2) - 1));
        let r = 0, g = 0, b = 0;
        if (hh >= 0 && hh < 1) { r = c; g = x; b = 0; }
        else if (hh < 2) { r = x; g = c; b = 0; }
        else if (hh < 3) { r = 0; g = c; b = x; }
        else if (hh < 4) { r = 0; g = x; b = c; }
        else if (hh < 5) { r = x; g = 0; b = c; }
        else { r = c; g = 0; b = x; }
        const m = l - c / 2;
        const a = typeof color.a === 'number' ? color.a : 1;
        return `rgba(${Math.round((r + m) * 255)}, ${Math.round((g + m) * 255)}, ${Math.round((b + m) * 255)}, ${a})`;
    }
    return null;
};

const readInputFieldStrokeAppearance = (
    engine: CreativeEngine,
    blockId: number
): {
    enabled: boolean | null;
    width: number | null;
    color: string | null;
    style: string | null;
    join: string | null;
    position: string | null;
} => {
    let enabled: boolean | null = null;
    let width: number | null = null;
    let color: string | null = null;
    let style: string | null = null;
    let join: string | null = null;
    let position: string | null = null;

    try {
        const rawEnabled = engine.block.getBool?.(blockId, 'stroke/enabled');
        if (typeof rawEnabled === 'boolean') {
            enabled = rawEnabled;
        }
    } catch {}

    try {
        const rawWidth = engine.block.getFloat?.(blockId, 'stroke/width');
        if (typeof rawWidth === 'number' && Number.isFinite(rawWidth)) {
            width = Math.max(0, rawWidth);
        }
    } catch {}

    try {
        const rawColor = engine.block.getColor?.(blockId, 'stroke/color');
        const rgba = colorToRgbaString(rawColor);
        if (rgba) {
            color = rgba;
        }
    } catch {}

    try {
        const rawStyle = engine.block.getStrokeStyle?.(blockId)
            ?? engine.block.getEnum?.(blockId, 'stroke/style');
        if (typeof rawStyle === 'string' && rawStyle) {
            style = rawStyle;
        }
    } catch {}

    try {
        const rawJoin = engine.block.getStrokeCornerGeometry?.(blockId)
            ?? engine.block.getEnum?.(blockId, 'stroke/cornerGeometry');
        if (typeof rawJoin === 'string' && rawJoin) {
            join = rawJoin;
        }
    } catch {}

    try {
        const rawPosition = engine.block.getStrokePosition?.(blockId)
            ?? engine.block.getEnum?.(blockId, 'stroke/position');
        if (typeof rawPosition === 'string' && rawPosition) {
            position = rawPosition;
        }
    } catch {}

    return { enabled, width, color, style, join, position };
};

const SVG_NS = 'http://www.w3.org/2000/svg';

const clamp01 = (value: number): number => Math.min(1, Math.max(0, value));

const parseCssColor = (color: string): { r: number; g: number; b: number; a: number } | null => {
    if (!color) return null;
    const raw = color.trim().toLowerCase();
    if (raw === 'transparent') {
        return { r: 0, g: 0, b: 0, a: 0 };
    }

    if (raw.startsWith('#')) {
        let hex = raw.slice(1);
        if (hex.length === 3) {
            hex = hex.split('').map((c) => c + c).join('');
        }
        if (hex.length === 6) {
            const r = parseInt(hex.slice(0, 2), 16);
            const g = parseInt(hex.slice(2, 4), 16);
            const b = parseInt(hex.slice(4, 6), 16);
            return { r, g, b, a: 1 };
        }
    }

    const match = raw.match(/^rgba?\((.+)\)$/);
    if (match) {
        const parts = match[1].split(',').map((part) => part.trim());
        if (parts.length >= 3) {
            const toChannel = (part: string) => {
                if (part.endsWith('%')) {
                    return clamp01(parseFloat(part) / 100) * 255;
                }
                return Math.max(0, Math.min(255, parseFloat(part)));
            };
            const toAlpha = (part: string) => {
                if (part.endsWith('%')) {
                    return clamp01(parseFloat(part) / 100);
                }
                return clamp01(parseFloat(part));
            };
            const r = toChannel(parts[0]);
            const g = toChannel(parts[1]);
            const b = toChannel(parts[2]);
            const a = parts.length >= 4 ? toAlpha(parts[3]) : 1;
            return { r, g, b, a };
        }
    }

    return null;
};

const applyOpacityToCssColor = (color: string, opacity: number): string | null => {
    const parsed = parseCssColor(color);
    if (!parsed) return null;
    const a = clamp01(parsed.a * opacity);
    return `rgba(${Math.round(parsed.r)}, ${Math.round(parsed.g)}, ${Math.round(parsed.b)}, ${a})`;
};

const readNumber = (value: string | undefined, fallback: number): number => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
};

const mapInputFieldStrokeStyleToSvg = (
    strokeStyle: string | undefined | null,
    strokeWidth: number
): { dasharray?: string; linecap: 'butt' | 'round' } => {
    const width = Math.max(1, strokeWidth);
    switch (strokeStyle) {
        case 'Dashed':
            return { dasharray: `${width * 3} ${width * 2.4}`, linecap: 'butt' };
        case 'DashedRound':
            return { dasharray: `${width * 3} ${width * 2.8}`, linecap: 'round' };
        case 'Dotted':
            return { dasharray: `0 ${width * 2.2}`, linecap: 'round' };
        case 'LongDashed':
            return { dasharray: `${width * 6} ${width * 3}`, linecap: 'butt' };
        case 'LongDashedRound':
            return { dasharray: `${width * 6} ${width * 3.4}`, linecap: 'round' };
        case 'Solid':
        default:
            return { linecap: 'butt' };
    }
};

const mapInputFieldStrokeJoinToSvg = (join: string | undefined | null): 'bevel' | 'miter' | 'round' => {
    switch (join) {
        case 'Bevel':
            return 'bevel';
        case 'Round':
            return 'round';
        case 'Miter':
        default:
            return 'miter';
    }
};

const mapInputFieldStrokePosition = (
    position: string | undefined | null
): 'Inner' | 'Center' | 'Outer' => {
    switch (position) {
        case 'Inner':
        case 'Outer':
            return position;
        case 'Center':
        default:
            return 'Center';
    }
};

const buildRoundedRectPath = (
    x: number,
    y: number,
    width: number,
    height: number,
    radius: number
): string => {
    if (width <= 0 || height <= 0) return '';
    const r = Math.max(0, Math.min(radius, width / 2, height / 2));
    const right = x + width;
    const bottom = y + height;
    if (r <= 0) {
        return `M ${x} ${y} H ${right} V ${bottom} H ${x} Z`;
    }
    return [
        `M ${x + r} ${y}`,
        `H ${right - r}`,
        `A ${r} ${r} 0 0 1 ${right} ${y + r}`,
        `V ${bottom - r}`,
        `A ${r} ${r} 0 0 1 ${right - r} ${bottom}`,
        `H ${x + r}`,
        `A ${r} ${r} 0 0 1 ${x} ${bottom - r}`,
        `V ${y + r}`,
        `A ${r} ${r} 0 0 1 ${x + r} ${y}`,
        'Z'
    ].join(' ');
};

const buildRoundedRectSegmentedPath = (
    x: number,
    y: number,
    width: number,
    height: number,
    radius: number
): string => {
    if (width <= 0 || height <= 0) return '';
    const r = Math.max(0, Math.min(radius, width / 2, height / 2));
    const right = x + width;
    const bottom = y + height;
    if (r <= 0) {
        return [
            `M ${x} ${y} H ${right}`,
            `M ${right} ${y} V ${bottom}`,
            `M ${right} ${bottom} H ${x}`,
            `M ${x} ${bottom} V ${y}`
        ].join(' ');
    }
    return [
        `M ${x + r} ${y} H ${right - r}`,
        `M ${right - r} ${y} A ${r} ${r} 0 0 1 ${right} ${y + r}`,
        `M ${right} ${y + r} V ${bottom - r}`,
        `M ${right} ${bottom - r} A ${r} ${r} 0 0 1 ${right - r} ${bottom}`,
        `M ${right - r} ${bottom} H ${x + r}`,
        `M ${x + r} ${bottom} A ${r} ${r} 0 0 1 ${x} ${bottom - r}`,
        `M ${x} ${bottom - r} V ${y + r}`,
        `M ${x} ${y + r} A ${r} ${r} 0 0 1 ${x + r} ${y}`
    ].join(' ');
};

const updateInputFieldSelectionHandleOverlay = (wrap: HTMLElement): void => {
    let overlay = wrap.querySelector('.input-field-selection-handles') as HTMLDivElement | null;
    if (!overlay) {
        overlay = document.createElement('div');
        overlay.className = 'input-field-selection-handles';
        overlay.setAttribute('aria-hidden', 'true');
        Object.assign(overlay.style, {
            position: 'absolute',
            inset: '0',
            overflow: 'visible',
            pointerEvents: 'none',
            zIndex: '4',
            display: 'none'
        });
        const overlayEl = overlay;
        const cornerSize = '11px';
        const edgeHorizontalWidth = '18px';
        const edgeHorizontalHeight = '8px';
        const edgeVerticalWidth = '8px';
        const edgeVerticalHeight = '18px';
        const handleBorder = '2px solid #3f66ff';

        const createHandle = (
            role: string,
            width: string,
            height: string,
            extra: Partial<CSSStyleDeclaration>
        ) => {
            const handle = document.createElement('div');
            handle.dataset.role = role;
            Object.assign(handle.style, {
                position: 'absolute',
                width,
                height,
                background: '#ffffff',
                border: handleBorder,
                boxSizing: 'border-box',
                borderRadius: role.startsWith('corner') ? '999px' : '999px',
                pointerEvents: 'none',
                ...extra
            });
            overlayEl.appendChild(handle);
        };

        createHandle('corner-top-left', cornerSize, cornerSize, {
            left: '0',
            top: '0',
            transform: 'translate(-50%, -50%)'
        });
        createHandle('corner-top-right', cornerSize, cornerSize, {
            right: '0',
            top: '0',
            transform: 'translate(50%, -50%)'
        });
        createHandle('corner-bottom-right', cornerSize, cornerSize, {
            right: '0',
            bottom: '0',
            transform: 'translate(50%, 50%)'
        });
        createHandle('corner-bottom-left', cornerSize, cornerSize, {
            left: '0',
            bottom: '0',
            transform: 'translate(-50%, 50%)'
        });
        createHandle('edge-top', edgeHorizontalWidth, edgeHorizontalHeight, {
            left: '50%',
            top: '0',
            transform: 'translate(-50%, -50%)'
        });
        createHandle('edge-right', edgeVerticalWidth, edgeVerticalHeight, {
            right: '0',
            top: '50%',
            transform: 'translate(50%, -50%)'
        });
        createHandle('edge-bottom', edgeHorizontalWidth, edgeHorizontalHeight, {
            left: '50%',
            bottom: '0',
            transform: 'translate(-50%, 50%)'
        });
        createHandle('edge-left', edgeVerticalWidth, edgeVerticalHeight, {
            left: '0',
            top: '50%',
            transform: 'translate(-50%, -50%)'
        });

        wrap.appendChild(overlayEl);
    }

    const overlayEl = overlay;
    overlayEl.style.display = wrap.classList.contains('is-selected') ? 'block' : 'none';
};

const updateInputFieldBorderLayer = (wrap: HTMLElement): void => {
    const svg = wrap.querySelector('.input-field-border-layer') as SVGSVGElement | null;
    const path = wrap.querySelector('.input-field-border-shape') as SVGPathElement | null;
    if (!svg || !path) return;

    const borderDisabled = wrap.dataset.disableBorder === '1';
    const hasError = wrap.classList.contains('has-error');
    const strokeWidth = Math.max(
        0,
        readNumber(
            wrap.dataset.renderBorderWidth,
            readNumber(wrap.dataset.baseBorderWidth, 0)
        )
    );
    const strokeColor = wrap.dataset.renderBorderColor || wrap.dataset.baseBorderColor || '';
    const width = Math.max(0, wrap.clientWidth);
    const height = Math.max(0, wrap.clientHeight);

    if (borderDisabled || hasError || strokeWidth <= 0 || !strokeColor || width <= 0 || height <= 0) {
        svg.style.display = 'none';
        return;
    }

    const radius = Math.max(
        0,
        readNumber(wrap.dataset.renderRadius, readNumber(wrap.dataset.baseRadius, 0))
    );
    const strokePosition = mapInputFieldStrokePosition(wrap.dataset.baseBorderPosition);
    const strokeStyle = wrap.dataset.baseBorderStyle || 'Solid';
    const strokeJoin = wrap.dataset.baseBorderJoin || 'Miter';
    const presentation = mapInputFieldStrokeStyleToSvg(strokeStyle, strokeWidth);

    const overflow = strokePosition === 'Outer' ? strokeWidth : strokePosition === 'Center' ? strokeWidth / 2 : 0;
    const svgWidth = width + overflow * 2;
    const svgHeight = height + overflow * 2;
    let x = strokeWidth / 2;
    let y = strokeWidth / 2;
    let boxWidth = width;
    let boxHeight = height;
    let pathRadius = radius;
    if (strokePosition === 'Inner') {
        boxWidth = Math.max(0, width - strokeWidth);
        boxHeight = Math.max(0, height - strokeWidth);
        pathRadius = Math.max(0, radius - strokeWidth / 2);
    } else if (strokePosition === 'Outer') {
        boxWidth = width + strokeWidth;
        boxHeight = height + strokeWidth;
        pathRadius = Math.max(0, radius + strokeWidth / 2);
    }
    const useSegmentedPath = presentation.dasharray != null;
    const d = useSegmentedPath
        ? buildRoundedRectSegmentedPath(x, y, boxWidth, boxHeight, pathRadius)
        : buildRoundedRectPath(x, y, boxWidth, boxHeight, pathRadius);
    if (!d) {
        svg.style.display = 'none';
        return;
    }

    svg.style.display = 'block';
    svg.style.left = `${-overflow}px`;
    svg.style.top = `${-overflow}px`;
    svg.style.width = `${svgWidth}px`;
    svg.style.height = `${svgHeight}px`;
    svg.setAttribute('viewBox', `0 0 ${svgWidth} ${svgHeight}`);
    path.setAttribute('d', d);
    path.setAttribute('stroke', strokeColor);
    path.setAttribute('stroke-width', `${strokeWidth}`);
    path.setAttribute('stroke-linecap', presentation.linecap);
    path.setAttribute('stroke-linejoin', mapInputFieldStrokeJoinToSvg(strokeJoin));
    path.setAttribute('stroke-miterlimit', '10');
    if (presentation.dasharray) {
        path.setAttribute('stroke-dasharray', presentation.dasharray);
    } else {
        path.removeAttribute('stroke-dasharray');
    }
};

const mapFontWeightToCss = (weight: string | undefined): string | null => {
    if (!weight) return null;
    const table: Record<string, string> = {
        thin: '100',
        extraLight: '200',
        light: '300',
        normal: '400',
        medium: '500',
        semiBold: '600',
        bold: '700',
        extraBold: '800',
        heavy: '900'
    };
    return table[weight] ?? weight;
};

const alignmentToTextAlign = (align: string | undefined | null): string | null => {
    if (!align) return null;
    const lower = String(align).toLowerCase();
    if (lower === 'left' || lower === 'center' || lower === 'right') {
        return lower;
    }
    return null;
};

const loadedFontKeys = new Set<string>();
const MIN_REASONABLE_FONT_PX = 8;

const ensureFontLoaded = (family: string, uri: string | undefined, weight?: string, style?: string) => {
    if (!uri || !family || typeof FontFace === 'undefined') return;
    const key = `${family}::${uri}::${weight || 'normal'}::${style || 'normal'}`;
    if (loadedFontKeys.has(key)) return;
    loadedFontKeys.add(key);
    try {
        const face = new FontFace(
            family,
            `url(${uri})`,
            {
                weight: weight || 'normal',
                style: style || 'normal'
            }
        );
        face.load().then(() => {
            try {
                (document as any).fonts?.add(face);
            } catch {}
        }).catch(() => {
            loadedFontKeys.delete(key);
        });
    } catch {
        loadedFontKeys.delete(key);
    }
};

const syncInputFieldAppearance = (engine: CreativeEngine | undefined, blockId: number | undefined, el: HTMLElement) => {
    if (!engine || blockId == null) return;
    const input = el.querySelector('.input-field-control') as HTMLElement | null;
    const fill = el.querySelector('.input-field-fill-layer') as HTMLElement | null;
    const label = el.querySelector('.input-field-label') as HTMLElement | null;
    if (!input && !label) return;

    const storedMeta = getStoredInputFieldMeta(engine, blockId);
    const meta = storedMeta ? withDefaults(storedMeta) : withDefaults({id: `input-field-${blockId}`});
    const metaFontSize = meta?.style?.fontSize;
    const metaStyle = meta?.style ?? {};
    const storedStyle = storedMeta?.style ?? {};

    try {
        const size = engine.block.getFloat?.(blockId, 'text/fontSize');
        if (typeof size === 'number' && size > 0) {
            let resolvedSize = size;
            if (typeof metaFontSize === 'number' && metaFontSize > 0) {
                if (size < MIN_REASONABLE_FONT_PX || size < metaFontSize * 0.5) {
                    resolvedSize = metaFontSize;
                }
            }
            const pageId = findOwningPage(engine, blockId);
            const pageWidth = pageId != null ? engine.block.getWidth?.(pageId) : undefined;
            if (typeof pageWidth === 'number' && pageWidth > 0) {
                const scaleFactor = PRESENTATION_WIDTH / pageWidth;
                if (Number.isFinite(scaleFactor) && Math.abs(scaleFactor - 1) > 0.01) {
                    resolvedSize = resolvedSize * scaleFactor;
                }
                el.dataset.pageWidth = String(pageWidth);
                el.dataset.pageScale = String(scaleFactor);
            }
            el.dataset.baseFontSize = String(resolvedSize);

            if (el.dataset.syncMeta === '1' && size >= MIN_REASONABLE_FONT_PX) {
                const lastSynced = Number(el.dataset.syncedFontSize);
                if (!Number.isFinite(lastSynced) || Math.abs(lastSynced - size) > 0.25) {
                    const nextMeta: InputFieldMeta = {
                        ...meta,
                        style: {
                            ...meta.style,
                            fontSize: size
                        }
                    };
                    setInputFieldMeta(engine, blockId, nextMeta);
                    el.dataset.syncedFontSize = String(size);
                }
            }
        }
    } catch {}
    try {
        const height = engine.block.getHeight?.(blockId);
        if (typeof height === 'number' && height > 0) {
            el.dataset.baseHeight = String(height);
        }
    } catch {}

    let fontFamily = '';
    let rawTypeface = '';
    let fontWeight = '';
    let fontStyle = '';
    let typefaceObj: any = null;
    try {
        const typefaces = engine.block.getTypefaces?.(blockId);
        if (Array.isArray(typefaces) && typefaces.length) {
            typefaceObj = typefaces[0];
            if (typefaceObj?.name) fontFamily = typefaceObj.name;
        }
    } catch {}
    if (!typefaceObj) {
        try {
            const typeface = engine.block.getTypeface?.(blockId);
            if (typeface) {
                typefaceObj = typeface;
            }
            if (typeface?.name) fontFamily = typeface.name;
        } catch {}
    }
    try { rawTypeface = engine.block.getString?.(blockId, 'text/typeface') ?? ''; } catch {}
    if (!fontFamily) {
        fontFamily = rawTypeface;
    }

    try {
        const weights = engine.block.getTextFontWeights?.(blockId);
        fontWeight = weights?.[0] ?? '';
    } catch {}
    try {
        const styles = engine.block.getTextFontStyles?.(blockId);
        fontStyle = styles?.[0] ?? '';
    } catch {}

    let fontUri = '';
    try { fontUri = engine.block.getString?.(blockId, 'text/fontFileUri') ?? ''; } catch {}

    if (!fontUri && typefaceObj?.fonts?.length) {
        const desiredWeight = fontWeight || 'normal';
        const desiredStyle = fontStyle || 'normal';
        const match =
            typefaceObj.fonts.find((f: any) => (f.weight ?? 'normal') === desiredWeight && (f.style ?? 'normal') === desiredStyle)
            ?? typefaceObj.fonts.find((f: any) => (f.weight ?? 'normal') === desiredWeight)
            ?? typefaceObj.fonts.find((f: any) => (f.style ?? 'normal') === desiredStyle)
            ?? typefaceObj.fonts[0];
        if (match?.uri) {
            fontUri = match.uri;
        }
        if (match?.weight) {
            fontWeight = match.weight;
        }
        if (match?.style) {
            fontStyle = match.style;
        }
    }

    const cssWeight = mapFontWeightToCss(fontWeight);
    const cleanedTypeface = rawTypeface.replace(/^Tf\s+/i, '').trim();
    const preferredFamily = cleanedTypeface || fontFamily || rawTypeface;
    const fontFamilyCss = preferredFamily ? `"${preferredFamily}"` : '';

    if (input) {
        if (fontFamilyCss) input.style.setProperty('font-family', fontFamilyCss, 'important');
        if (cssWeight) input.style.fontWeight = cssWeight;
        if (fontStyle) input.style.fontStyle = fontStyle;
    }
    if (label) {
        if (fontFamilyCss) label.style.setProperty('font-family', fontFamilyCss, 'important');
        if (cssWeight) label.style.fontWeight = cssWeight;
        if (fontStyle) label.style.fontStyle = fontStyle;
    }

    try {
        let color: any = null;
        try {
            const colors = engine.block.getTextColors?.(blockId);
            if (colors && colors.length) {
                color = colors[0];
            }
        } catch {}
        if (!color) {
            const fill = engine.block.getFill?.(blockId);
            color = fill ? engine.block.getColor?.(fill, 'fill/solid/color') : null;
        }
        const rgba = colorToRgbaString(color);
        const alpha = typeof (color as any)?.a === 'number' ? (color as any).a : 1;
        if (rgba && alpha > 0) {
            if (input) input.style.color = rgba;
        }
    } catch {}

    try {
        const align = engine.block.getEnum?.(blockId, 'text/horizontalAlignment') as string | undefined;
        const cssAlign = alignmentToTextAlign(align);
        if (cssAlign && input) {
            input.style.textAlign = cssAlign;
        }
    } catch {}

    try {
        let opacity: number | undefined;
        const metaOpacity = meta?.style?.opacity;
        if (typeof metaOpacity === 'number' && Number.isFinite(metaOpacity) && metaOpacity !== 1) {
            opacity = metaOpacity;
        }
        try {
            const stored = engine.block.getMetadata?.(blockId, INPUT_FIELD_OPACITY_KEY);
            const storedValue = Number(stored);
            if (Number.isFinite(storedValue)) {
                opacity = storedValue;
            }
        } catch {}
        if (opacity == null) {
            try {
                const next = engine.block.getOpacity?.(blockId);
                if (typeof next === 'number' && Number.isFinite(next)) {
                    opacity = next;
                }
            } catch {}
        }
        if (opacity == null) {
            const next = engine.block.getFloat?.(blockId, 'opacity');
            if (typeof next === 'number' && Number.isFinite(next)) {
                opacity = next;
            }
        }
        if (typeof opacity === 'number' && !Number.isNaN(opacity)) {
            const lastOpacity = Number(el.dataset.lastOpacity);
            if (!Number.isFinite(lastOpacity) || lastOpacity !== opacity) {
                el.dataset.lastOpacity = String(opacity);
                if (opacity !== 1) {
                    el.dataset.opacityOverride = '1';
                }
            }
            const allowZero = el.dataset.opacityOverride === '1';
            const resolvedOpacity = opacity > 0 || allowZero ? opacity : 1;
            el.dataset.opacityValue = String(clamp01(resolvedOpacity));
        }
    } catch {}

    try {
        const stroke = readInputFieldStrokeAppearance(engine, blockId);
        const resolvedBorderWidth = storedStyle.borderWidth ?? stroke.width ?? metaStyle.borderWidth;
        const resolvedBorderColor = storedStyle.borderColor ?? stroke.color ?? metaStyle.borderColor;
        const resolvedBorderStyle = storedStyle.borderStyle ?? stroke.style ?? metaStyle.borderStyle;
        const resolvedBorderJoin = storedStyle.borderJoin ?? stroke.join ?? metaStyle.borderJoin;
        const resolvedBorderPosition = storedStyle.borderPosition ?? stroke.position ?? metaStyle.borderPosition;
        const resolvedStrokeEnabled = storedStyle.strokeEnabled ?? stroke.enabled ?? metaStyle.strokeEnabled;

        if (resolvedBorderWidth != null) {
            el.dataset.baseBorderWidth = String(resolvedBorderWidth);
        }
        if (resolvedBorderColor) {
            el.dataset.baseBorderColor = resolvedBorderColor;
        }
        if (resolvedBorderStyle) {
            el.dataset.baseBorderStyle = resolvedBorderStyle;
        }
        if (resolvedBorderJoin) {
            el.dataset.baseBorderJoin = resolvedBorderJoin;
        }
        if (resolvedBorderPosition) {
            el.dataset.baseBorderPosition = resolvedBorderPosition;
        }
        if (resolvedStrokeEnabled === true) {
            el.dataset.disableBorder = '0';
        } else if (resolvedStrokeEnabled === false) {
            el.dataset.disableBorder = '1';
        }

        if (el.dataset.syncMeta === '1') {
            const nextMetaStyle = { ...metaStyle };
            let metaDirty = false;

            if (resolvedBorderWidth != null && nextMetaStyle.borderWidth !== resolvedBorderWidth) {
                nextMetaStyle.borderWidth = resolvedBorderWidth;
                metaDirty = true;
            }
            if (resolvedBorderColor && nextMetaStyle.borderColor !== resolvedBorderColor) {
                nextMetaStyle.borderColor = resolvedBorderColor;
                metaDirty = true;
            }
            if (resolvedBorderStyle && nextMetaStyle.borderStyle !== resolvedBorderStyle) {
                nextMetaStyle.borderStyle = resolvedBorderStyle;
                metaDirty = true;
            }
            if (resolvedBorderJoin && nextMetaStyle.borderJoin !== resolvedBorderJoin) {
                nextMetaStyle.borderJoin = resolvedBorderJoin;
                metaDirty = true;
            }
            if (resolvedBorderPosition && nextMetaStyle.borderPosition !== resolvedBorderPosition) {
                nextMetaStyle.borderPosition = resolvedBorderPosition;
                metaDirty = true;
            }
            if (resolvedStrokeEnabled != null && nextMetaStyle.strokeEnabled !== resolvedStrokeEnabled) {
                nextMetaStyle.strokeEnabled = resolvedStrokeEnabled;
                metaDirty = true;
            }

            if (metaDirty) {
                setInputFieldMeta(engine, blockId, {
                    ...meta,
                    style: nextMetaStyle
                });
            }
        }
    } catch {}

    try {
        const bgEnabled = engine.block.getBool?.(blockId, 'backgroundColor/enabled');
        if (bgEnabled) {
            const bgColor = engine.block.getColor?.(blockId, 'backgroundColor/color');
            const rgba = colorToRgbaString(bgColor);
            const padLeft = engine.block.getFloat?.(blockId, 'backgroundColor/paddingLeft');
            const padRight = engine.block.getFloat?.(blockId, 'backgroundColor/paddingRight');
            const padTop = engine.block.getFloat?.(blockId, 'backgroundColor/paddingTop');
            const padBottom = engine.block.getFloat?.(blockId, 'backgroundColor/paddingBottom');
            const padX = (typeof padLeft === 'number' || typeof padRight === 'number')
                ? ((Number(padLeft) || 0) + (Number(padRight) || 0)) / 2
                : undefined;
            const padY = (typeof padTop === 'number' || typeof padBottom === 'number')
                ? ((Number(padTop) || 0) + (Number(padBottom) || 0)) / 2
                : undefined;
            const radius = engine.block.getFloat?.(blockId, 'backgroundColor/cornerRadius');

            const resolvedBackground = storedStyle.backgroundColor ?? rgba ?? metaStyle.backgroundColor;
            const resolvedPaddingX = storedStyle.paddingX ?? padX ?? metaStyle.paddingX ?? metaStyle.padding;
            const resolvedPaddingY = storedStyle.paddingY ?? padY ?? metaStyle.paddingY ?? metaStyle.padding;
            const resolvedRadius = storedStyle.borderRadius ?? radius ?? metaStyle.borderRadius;

            if (resolvedBackground) {
                el.dataset.baseBackground = resolvedBackground;
                if (fill) {
                    fill.style.background = 'transparent';
                }
            }
            if (resolvedPaddingX != null && !Number.isNaN(resolvedPaddingX)) {
                el.dataset.basePaddingX = String(resolvedPaddingX);
            }
            if (resolvedPaddingY != null && !Number.isNaN(resolvedPaddingY)) {
                el.dataset.basePaddingY = String(resolvedPaddingY);
            }
            if (resolvedRadius != null && !Number.isNaN(resolvedRadius)) {
                el.dataset.baseRadius = String(resolvedRadius);
            }
            if (input) {
                input.style.background = 'transparent';
            }
        } else {
            const fallback = el.dataset.baseBackground;
            if (fill && fallback) {
                const opacityValue = Number(el.dataset.opacityValue);
                const opacityScale = Number.isFinite(opacityValue) ? clamp01(opacityValue) : 1;
                fill.style.background = applyOpacityToCssColor(fallback, opacityScale) ?? fallback;
            }
            if (input) {
                input.style.background = 'transparent';
            }
        }
    } catch {}

    try {
        if (input) {
            const opacityValue = Number(el.dataset.opacityValue);
            const opacityScale = Number.isFinite(opacityValue) ? clamp01(opacityValue) : 1;
            const baseBorder = el.dataset.baseBorderColor || input.style.borderColor;
            const nextBorder = baseBorder ? applyOpacityToCssColor(baseBorder, opacityScale) : null;
            if (nextBorder) {
                el.dataset.renderBorderColor = nextBorder;
            }
            updateInputFieldBorderLayer(el);
            input.style.opacity = '1';
        }
    } catch {}

    try {
        const shadowEnabled = engine.block.getBool?.(blockId, 'dropShadow/enabled');
        if (shadowEnabled) {
            const shadowColor = engine.block.getColor?.(blockId, 'dropShadow/color');
            const rgba = colorToRgbaString(shadowColor);
            if (rgba) {
                el.dataset.shadowColor = rgba;
            }
            const offsetX = engine.block.getFloat?.(blockId, 'dropShadow/offset/x');
            const offsetY = engine.block.getFloat?.(blockId, 'dropShadow/offset/y');
            const blurX = engine.block.getFloat?.(blockId, 'dropShadow/blurRadius/x');
            const blurY = engine.block.getFloat?.(blockId, 'dropShadow/blurRadius/y');
            if (typeof offsetX === 'number') el.dataset.baseShadowX = String(offsetX);
            if (typeof offsetY === 'number') el.dataset.baseShadowY = String(offsetY);
            if (typeof blurX === 'number' || typeof blurY === 'number') {
                const blur = ((Number(blurX) || 0) + (Number(blurY) || 0)) / 2;
                el.dataset.baseShadowBlur = String(blur);
            }
        } else {
            el.dataset.baseShadowX = '0';
            el.dataset.baseShadowY = '0';
            el.dataset.baseShadowBlur = '0';
            el.dataset.shadowColor = '';
        }
    } catch {}

    try {
        if (input) {
            const inAnim = engine.block.getInAnimation?.(blockId);
            const loopAnim = engine.block.getLoopAnimation?.(blockId);
            const outAnim = engine.block.getOutAnimation?.(blockId);
            const animKeyParts: string[] = [];
            if (inAnim && engine.block.isValid?.(inAnim)) {
                animKeyParts.push(String(engine.block.getType?.(inAnim) ?? inAnim));
            }
            if (loopAnim && engine.block.isValid?.(loopAnim)) {
                animKeyParts.push(String(engine.block.getType?.(loopAnim) ?? loopAnim));
            }
            if (outAnim && engine.block.isValid?.(outAnim)) {
                animKeyParts.push(String(engine.block.getType?.(outAnim) ?? outAnim));
            }
            const nextKey = animKeyParts.join('|');
            const prevKey = el.dataset.animationKey ?? '';
            if (nextKey) {
                if (nextKey !== prevKey) {
                    input.classList.remove('input-field-anim-preview');
                    void (input as HTMLElement).offsetWidth;
                    input.classList.add('input-field-anim-preview');
                    el.dataset.animationKey = nextKey;
                }
            } else {
                input.classList.remove('input-field-anim-preview');
                el.dataset.animationKey = '';
            }
        }
    } catch {}

    try {
        if (preferredFamily) {
            try {
                (document as any).fonts?.load?.(`${cssWeight ?? '400'} 12px "${preferredFamily}"`);
            } catch {}
        }
        if (fontUri && preferredFamily) {
            let resolvedUri = fontUri;
            try {
                if (!/^[a-z]+:/i.test(fontUri) && engine.editor?.getAbsoluteURI) {
                    resolvedUri = engine.editor.getAbsoluteURI(fontUri);
                }
            } catch {}
            ensureFontLoaded(preferredFamily, resolvedUri, cssWeight ?? undefined, fontStyle || undefined);
        }
    } catch {}
};

export function updateInputFieldOverlayGeometry(el: HTMLElement, engine?: CreativeEngine, blockId?: number): void {
    if (!el || el.dataset.idecideOverlay !== 'input-field') return;

    if (engine && blockId != null) {
        syncInputFieldAppearance(engine, blockId, el);
    }
    const baseH = Number(el.dataset.baseHeight) || 50;
    const baseFont = Number(el.dataset.baseFontSize) || 16;
    const basePadding = Number(el.dataset.basePadding) || 10;
    const basePaddingX = Number(el.dataset.basePaddingX) || basePadding;
    const basePaddingY = Number(el.dataset.basePaddingY) || basePadding;
    const baseRadius = Number(el.dataset.baseRadius) || 4;
    const baseBorder = readNumber(el.dataset.baseBorderWidth, 1);
    const borderDisabled = el.dataset.disableBorder === '1';

    const h = el.clientHeight || baseH;
    const scale = h / baseH;

    const fontPx = Math.max(10, Math.round(baseFont * scale));
    const paddingX = Math.max(2, Math.round(basePaddingX * scale));
    const paddingY = Math.max(2, Math.round(basePaddingY * scale));
    const radiusPx = Math.max(0, Math.round(baseRadius * scale));
    const borderPx = borderDisabled ? 0 : Math.max(0, Math.round(baseBorder * scale));
    const validationBorderPx = Math.max(1, Math.round(Math.max(baseBorder, 1) * scale));
    const hasError = el.classList.contains('has-error');

    el.dataset.renderBorderWidth = String(borderPx);
    el.dataset.renderRadius = String(radiusPx);

    const input = el.querySelector('.input-field-control') as HTMLElement | null;
    const fill = el.querySelector('.input-field-fill-layer') as HTMLElement | null;
    if (input) {
        input.style.fontSize = `${fontPx}px`;
        input.style.padding = `${paddingY}px ${paddingX}px`;
        input.style.borderRadius = `${radiusPx}px`;
        input.style.borderWidth = hasError ? `${validationBorderPx}px` : '0px';
        input.style.borderStyle = hasError ? 'solid' : 'none';
    }
    if (fill) {
        fill.style.borderRadius = `${radiusPx}px`;
    }

    updateInputFieldBorderLayer(el);

    const label = el.querySelector('.input-field-label') as HTMLElement | null;
    if (label) {
        label.style.fontSize = `${Math.max(10, Math.round(fontPx * 0.85))}px`;
    }

    const error = el.querySelector('.input-field-error') as HTMLElement | null;
    if (error) {
        error.style.fontSize = `${Math.max(9, Math.round(fontPx * 0.6))}px`;
    }

    const handle = el.querySelector('.input-field-drag-handle') as HTMLElement | null;
    if (handle) {
        const size = Math.max(20, Math.round(fontPx * 1.2));
        handle.style.width = `${size}px`;
        handle.style.height = `10px`;
        handle.style.top = el.classList.contains('has-error') ? 'calc(100% + 24px)' : 'calc(100% + 6px)';
        handle.style.marginTop = '5px';
    }

    const shadowTarget = fill ?? input;
    if (shadowTarget) {
        const shadowColor = el.dataset.shadowColor;
        const baseShadowX = Number(el.dataset.baseShadowX) || 0;
        const baseShadowY = Number(el.dataset.baseShadowY) || 0;
        const baseShadowBlur = Number(el.dataset.baseShadowBlur) || 0;
        if (shadowColor && (baseShadowX || baseShadowY || baseShadowBlur)) {
            const opacityValue = Number(el.dataset.opacityValue);
            const opacityScale = Number.isFinite(opacityValue) ? clamp01(opacityValue) : 1;
            const resolvedShadow = applyOpacityToCssColor(shadowColor, opacityScale) ?? shadowColor;
            const shadowX = Math.round(baseShadowX * scale);
            const shadowY = Math.round(baseShadowY * scale);
            const shadowBlur = Math.max(0, Math.round(baseShadowBlur * scale));
            shadowTarget.style.boxShadow = `${shadowX}px ${shadowY}px ${shadowBlur}px ${resolvedShadow}`;
        } else {
            shadowTarget.style.boxShadow = 'none';
        }
    }

}

export function refreshInputFieldOverlay(engine: CreativeEngine, inputFieldId: number): void {
    const oldEl = INPUT_FIELD_OVERLAYS.get(inputFieldId);
    if (!oldEl) return;
    const root = oldEl.parentElement as HTMLElement | null;
    const isInteractive = oldEl.dataset.interactive === '1';
    const isDraggable = oldEl.dataset.draggable === '1';
    const shouldReplaceTokens = oldEl.dataset.replaceTokens === '1';
    oldEl.remove();
    if (!root) {
        INPUT_FIELD_OVERLAYS.delete(inputFieldId);
        return;
    }
    const next = createInputFieldOverlay(
        engine,
        inputFieldId,
        root,
        isInteractive,
        isDraggable,
        shouldReplaceTokens
    );
    INPUT_FIELD_OVERLAYS.set(inputFieldId, next);
    requestAnimationFrame(() => positionOverlayToBlock(engine, inputFieldId, next, updateInputFieldOverlayGeometry));
}

export type InputFieldSubmissionValue = {
    name: string;
    value: string;
    label?: string;
};

export type InputFieldSubmissionResult = {
    valid: boolean;
    values: InputFieldSubmissionValue[];
};

export async function validateRequiredInputFields(
    engine: CreativeEngine,
    pageId: number,
    opts: { replaceTokens?: boolean; tokenContext?: TokenContext } = {}
): Promise<boolean> {
    const fieldIds = findInputFieldsForPage(engine, pageId);
    let allValid = true;

    for (const fieldId of fieldIds) {
        const meta = getInputFieldMeta(engine, fieldId);
        if (!meta.isRequired) continue;

        let resolvedMessages: ResolvedInputFieldTokens | null = null;
        if (opts.replaceTokens) {
            resolvedMessages = resolveInputFieldTokens(meta, opts.tokenContext, engine);
        }

        const invalidMessage = resolvedMessages?.invalidMessage ?? meta.invalidMessage;
        const validationMeta: InputFieldMeta = {
            ...meta,
            invalidMessage,
            requiredMessage: invalidMessage || meta.requiredMessage
        };

        const overlay = INPUT_FIELD_OVERLAYS.get(fieldId);
        const inputEl = overlay?.querySelector('.input-field-control') as
            | HTMLInputElement
            | HTMLTextAreaElement
            | HTMLSelectElement
            | null;
        const errorEl = overlay?.querySelector('.input-field-error') as HTMLDivElement | null;
        const handleEl = overlay?.querySelector('.input-field-drag-handle') as HTMLDivElement | null;

        let value = '';
        if (inputEl) {
            value = inputEl.value ?? '';
        } else {
            try {
                value = engine.variable?.getString?.(`input:${meta.varName ?? ''}`) ?? '';
            } catch {}
            if (!value && meta.inputDefault) {
                value = meta.inputDefault;
            }
        }

        const result = validateInputField(value, validationMeta);
        if (!result.valid) {
            allValid = false;
        }

        if (overlay && inputEl && errorEl) {
            const style = {...defaultStyle(), ...(meta.style ?? {})};
            updateValidationUI({
                wrap: overlay,
                inputEl,
                errorEl,
                handleEl,
                style,
                show: !result.valid,
                message: result.message || invalidMessage || 'Invalid value'
            });
        }
    }

    return allValid;
}

export async function collectInputFieldSubmission(
    engine: CreativeEngine,
    pageId: number,
    targetVarName: string,
    opts: { replaceTokens?: boolean; tokenContext?: TokenContext } = {}
): Promise<InputFieldSubmissionResult> {
    const trimmedVar = (targetVarName ?? '').trim();
    if (!trimmedVar) {
        return { valid: true, values: [] };
    }

    const fieldIds = findInputFieldsForPage(engine, pageId);
    const values: InputFieldSubmissionValue[] = [];
    let allValid = true;

    for (const fieldId of fieldIds) {
        const meta = getInputFieldMeta(engine, fieldId);
        const metaVarName = (meta.varName ?? '').trim();
        if (!metaVarName || metaVarName !== trimmedVar) {
            continue;
        }

        let resolvedMessages: ResolvedInputFieldTokens | null = null;
        if (opts.replaceTokens) {
            resolvedMessages = resolveInputFieldTokens(meta, opts.tokenContext, engine);
        }

        const validationMeta: InputFieldMeta = {
            ...meta,
            invalidMessage: resolvedMessages?.invalidMessage ?? meta.invalidMessage,
            requiredMessage: resolvedMessages?.requiredMessage ?? meta.requiredMessage
        };

        const overlay = INPUT_FIELD_OVERLAYS.get(fieldId);
        const inputEl = overlay?.querySelector('.input-field-control') as
            | HTMLInputElement
            | HTMLTextAreaElement
            | HTMLSelectElement
            | null;
        const errorEl = overlay?.querySelector('.input-field-error') as HTMLDivElement | null;
        const handleEl = overlay?.querySelector('.input-field-drag-handle') as HTMLDivElement | null;

        let value = '';
        if (inputEl) {
            value = inputEl.value ?? '';
        } else {
            try {
                value = engine.variable?.getString?.(`input:${metaVarName}`) ?? '';
            } catch {}
            if (!value && meta.inputDefault) {
                value = meta.inputDefault;
            }
        }

        const result = validateInputField(value, validationMeta);
        if (!result.valid) {
            allValid = false;
        }

        if (overlay && inputEl && errorEl) {
            const style = {...defaultStyle(), ...(meta.style ?? {})};
            updateValidationUI({
                wrap: overlay,
                inputEl,
                errorEl,
                handleEl,
                style,
                show: !result.valid,
                message: result.message || validationMeta.invalidMessage || 'Invalid value'
            });
        }

        values.push({ name: metaVarName, value, label: meta.label });
    }

    return { valid: allValid, values };
}

/**
 * Validation functions
 */
export function validateInputField(value: string, meta: InputFieldMeta): {valid: boolean; message?: string} {
    // Required check
    if (meta.isRequired && !value.trim()) {
        return {valid: false, message: meta.requiredMessage};
    }

    // Type-specific validation
    switch (meta.inputType) {
        case 'email':
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (value && !emailRegex.test(value)) {
                return {valid: false, message: meta.invalidMessage};
            }
            break;

        case 'url':
            try {
                if (value) {
                    const trimmed = value.trim();
                    if (/\s/.test(trimmed)) {
                        return {valid: false, message: meta.invalidMessage};
                    }
                    const hasScheme = /^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed);
                    const candidate = hasScheme ? trimmed : `https://${trimmed}`;
                    const parsed = new URL(candidate);
                    const hostname = parsed.hostname;
                    if (!hostname || !hostname.includes('.') || hostname.startsWith('.') || hostname.endsWith('.')) {
                        return {valid: false, message: meta.invalidMessage};
                    }
                }
            } catch {
                return {valid: false, message: meta.invalidMessage};
            }
            break;

        case 'phone':
            const phoneRegex = /^[\d\s\-\(\)]+$/;
            if (value && (!phoneRegex.test(value) || !/\d/.test(value))) {
                return {valid: false, message: meta.invalidMessage};
            }
            break;

        case 'number':
        case 'currency':
            const num = parseNumericValue(value);
            if (value && (num === null || Number.isNaN(num))) {
                return {valid: false, message: meta.invalidMessage};
            }
            if (num !== null && meta.validation?.min !== undefined && num < meta.validation.min) {
                return {valid: false, message: `Value must be at least ${meta.validation.min}`};
            }
            if (num !== null && meta.validation?.max !== undefined && num > meta.validation.max) {
                return {valid: false, message: `Value must be at most ${meta.validation.max}`};
            }
            break;
    }

    // Length validation (text-only)
    if (meta.inputType === 'text' || meta.inputType === 'multi') {
        if (meta.validation?.minLength && value.length < meta.validation.minLength) {
            return {valid: false, message: `Minimum length is ${meta.validation.minLength} characters`};
        }
        if (meta.validation?.maxLength && value.length > meta.validation.maxLength) {
            return {valid: false, message: `Maximum length is ${meta.validation.maxLength} characters`};
        }
    }

    // Custom pattern validation
    if (meta.validation?.pattern) {
        const regex = new RegExp(meta.validation.pattern);
        if (value && !regex.test(value)) {
            return {valid: false, message: meta.invalidMessage};
        }
    }

    return {valid: true};
}

/**
 * Format input values based on type
 */
export function formatInputValue(value: string, inputType: InputFieldType): string {
    switch (inputType) {
        case 'currency':
            const num = parseFloat(value.replace(/[^0-9.-]/g, ''));
            if (!isNaN(num)) {
                return new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'USD'
                }).format(num);
            }
            return value;

        case 'phone':
            const digits = value.replace(/\D/g, '');
            if (digits.length === 10) {
                return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
            }
            return value;

        default:
            return value;
    }
}

const parseNumericValue = (raw: string): number | null => {
    if (!raw) return null;
    const cleaned = raw.replace(/[^0-9.\-]/g, '');
    if (!cleaned) return null;
    const num = parseFloat(cleaned);
    return Number.isNaN(num) ? null : num;
};
